import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios";
import { useNotifications } from "@via/hooks/useNotifications";
import { ErrorNotification } from "@via/utils/interfaces";
import {
  ADMIN_ORGANIZATION_RUN_ENVIRONMENT_WRITABLE_FIELDS,
  ADMIN_SYSTEM_GENERATED_WRITABLE_FIELDS,
  AdminOrganizationRunEnvironment,
  GetAdminOrganizationRunEnvironmentResponseBody,
  ListAdminOrganizationCredentialsResponseBody,
  ListAdminOrganizationRunEnvironmentsResponseBody,
  type ProfileClusterForClient,
  AttachCustomerBoundaryResponseBody,
  GetAdminOrganizationCloudRequestParams,
  GetAdminOrganizationCloudResponseBody,
  GetAdminOrganizationFeatureFlagsRequestParams,
  GetAdminOrganizationFeatureFlagsResponseBody,
  PutAdminOrganizationFeatureFlagsResponseBody,
  MaterialiseOrganizationRunEnvironmentResponseBody,
  GetChargeHistoryResponseBodyItem,
  GetUserOrganizationDetailsForAdminResponseBody,
  CreateOrganizationForAdminRequestBody,
  CreateOrganizationForAdminResponseBody,
  UpdateOrganizationForAdminRequestBody,
  UpdateOrganizationForAdminRequestParams,
  CreateAdminOrganizationAllowedServiceAccountRequestBody,
  CreateAdminOrganizationAllowedServiceAccountRequestParams,
  CreateAdminOrganizationAllowedServiceAccountResponseBody,
  DeleteAdminOrganizationAllowedServiceAccountRequestParams,
  DeleteAdminOrganizationAllowedServiceAccountResponseBody,
  GetAdminOrganizationAllowedServiceAccountsRequestParams,
  GetAdminOrganizationAllowedServiceAccountsResponseBody,
  CreateAdminOrganizationAllowedRoleArnRequestBody,
  CreateAdminOrganizationAllowedRoleArnRequestParams,
  CreateAdminOrganizationAllowedRoleArnResponseBody,
  DeleteAdminOrganizationAllowedRoleArnRequestParams,
  DeleteAdminOrganizationAllowedRoleArnResponseBody,
  GetAdminOrganizationAllowedRoleArnsRequestParams,
  GetAdminOrganizationAllowedRoleArnsResponseBody,
  CreateAdminOrganizationAllowedDirPrefixRequestBody,
  CreateAdminOrganizationAllowedDirPrefixRequestParams,
  CreateAdminOrganizationAllowedDirPrefixResponseBody,
  DeleteAdminOrganizationAllowedDirPrefixRequestParams,
  DeleteAdminOrganizationAllowedDirPrefixResponseBody,
  GetAdminOrganizationAllowedDirPrefixesRequestParams,
  GetAdminOrganizationAllowedDirPrefixesResponseBody,
  AddAdminOrganizationMemberRequestBody,
  AddAdminOrganizationMemberRequestParams,
  AddAdminOrganizationMemberResponseBody,
  GetAdminOrganizationMembersRequestParams,
  GetAdminOrganizationMembersResponseBody,
  RemoveAdminOrganizationMemberRequestParams,
  RemoveAdminOrganizationMemberResponseBody,
  UpdateAdminOrganizationMemberRequestBody,
  UpdateAdminOrganizationMemberRequestParams,
  UpdateAdminOrganizationMemberResponseBody,
} from "common";

const fetchOrganizations =
  async (): Promise<GetUserOrganizationDetailsForAdminResponseBody> => {
    try {
      const baseUrl = "/api/admin/organization";

      const res = await axios.get(baseUrl);
      return res.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

const fetchOrganizationFeatureFlags = async (
  requestParams: GetAdminOrganizationFeatureFlagsRequestParams
): Promise<GetAdminOrganizationFeatureFlagsResponseBody> => {
  const res = await axios.get(
    `/api/admin/organization/${requestParams.organizationId}/feature-flags`
  );
  return res.data;
};

export const useGetOrganizationFeatureFlags = (
  requestParams: GetAdminOrganizationFeatureFlagsRequestParams,
  enabled: boolean
): UseQueryResult<GetAdminOrganizationFeatureFlagsResponseBody, Error> => {
  return useQuery<GetAdminOrganizationFeatureFlagsResponseBody, Error>({
    queryKey: [
      `/api/admin/organization/${requestParams.organizationId}/feature-flags`,
      requestParams,
    ],
    queryFn: () => fetchOrganizationFeatureFlags(requestParams),
    enabled,
    retry: false,
  });
};

/**
 * Written directly rather than through useOrganizationCloudWrite: the factory hardcodes the
 * cloud query key, and an entitlements save must refresh the entitlements catalog instead.
 * The server validates the DIFF against the stored set (spec 7.8), so this always sends the
 * full granted list; a rejection names the flag and reason in response.data.message, which
 * the error notification surfaces to the operator unedited.
 */
export const useReplaceOrganizationFeatureFlags = (): UseMutationResult<
  PutAdminOrganizationFeatureFlagsResponseBody,
  ErrorNotification,
  { organizationId: string; featureFlags: string[] }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    PutAdminOrganizationFeatureFlagsResponseBody,
    ErrorNotification,
    { organizationId: string; featureFlags: string[] }
  >({
    mutationFn: async ({ organizationId, featureFlags }) => {
      const res = await axios.put(
        `/api/admin/organization/${organizationId}/feature-flags`,
        { featureFlags }
      );
      return res.data;
    },
    onSuccess: (_data, variables): void => {
      setNotification({
        id: "organization-entitlements-saved",
        message: "Entitlements Saved",
        details: "The organization's granted entitlements were updated",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [
          `/api/admin/organization/${variables.organizationId}/feature-flags`,
        ],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-entitlements-save-failed",
        message: "Entitlements Save Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useGetOrganizations = (): UseQueryResult<
  GetUserOrganizationDetailsForAdminResponseBody,
  Error
> => {
  return useQuery<GetUserOrganizationDetailsForAdminResponseBody, Error>({
    queryKey: ["/api/admin/organization"],
    queryFn: () => fetchOrganizations(),
    retry: false,
  });
};

export const useCreateOrganization = (): UseMutationResult<
  CreateOrganizationForAdminResponseBody,
  ErrorNotification,
  CreateOrganizationForAdminRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: CreateOrganizationForAdminRequestBody) => {
      const res = await axios.post<CreateOrganizationForAdminResponseBody>(
        "/api/admin/organization",
        payload
      );
      return res.data;
    },
    onSuccess: (organization): void => {
      setNotification({
        id: "organization-created",
        message: "Organization Created",
        details: `${organization.name} is created`,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/organization"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-create-failed",
        message: "Organization Create Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useEditOrganization = (): UseMutationResult<
  void,
  ErrorNotification,
  UpdateOrganizationForAdminRequestParams &
    UpdateOrganizationForAdminRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (
      payload: UpdateOrganizationForAdminRequestParams &
        UpdateOrganizationForAdminRequestBody
    ) => {
      const res = await axios.patch(
        `/api/admin/organization/${payload.organizationId}`,
        {
          margin: payload.margin,
        }
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "user-organization-successfully-updated",
        message: "User Organization Margin is Updated",
        details: "User Organization margin is successfully updated",
        variant: NOTIFICATION.SUCCESS,
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/admin/organization"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "user-organization-update-failed",
        message: "Organization Margin Update Failed",
        details:
          error?.response?.data?.message ||
          "Organization margin could not be updated",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

const fetchOrganizationChargeHistory = async (): Promise<
  GetChargeHistoryResponseBodyItem[]
> => {
  try {
    const baseUrl = "/api/admin/charge-history";

    const res = await axios.get(baseUrl);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetOrganizationChargeHistory = (): UseQueryResult<
  GetChargeHistoryResponseBodyItem[],
  Error
> => {
  return useQuery<GetChargeHistoryResponseBodyItem[], Error>({
    queryKey: ["/api/admin/charge-history"],
    queryFn: () => fetchOrganizationChargeHistory(),
    retry: false,
  });
};

const fetchOrganizationCloud = async (
  requestParams: GetAdminOrganizationCloudRequestParams
): Promise<GetAdminOrganizationCloudResponseBody> => {
  const res = await axios.get(
    `/api/admin/organization/${requestParams.organizationId}/cloud`
  );
  return res.data;
};

export const useGetOrganizationCloud = (
  requestParams: GetAdminOrganizationCloudRequestParams,
  enabled: boolean
): UseQueryResult<GetAdminOrganizationCloudResponseBody, Error> => {
  return useQuery<GetAdminOrganizationCloudResponseBody, Error>({
    queryKey: [
      `/api/admin/organization/${requestParams.organizationId}/cloud`,
      requestParams,
    ],
    queryFn: () => fetchOrganizationCloud(requestParams),
    enabled,
    retry: false,
  });
};

/**
 * The Cloud tab's five write hooks. Attach, detach and Create reuse routes that already exist
 * elsewhere; what makes these modal-scoped is the invalidation: every write invalidates the
 * organization's cloud query (prefix match on the URL element, so the [url, params] key matches),
 * and the two attachment writes also refresh the Customer Boundary tab's list.
 */
const useOrganizationCloudWrite = <
  TVariables extends { organizationId: string },
  TData,
>(config: {
  mutationFn: (variables: TVariables) => Promise<TData>;
  successId: string;
  successMessage: string;
  successDetails: string;
  errorId: string;
  errorMessage: string;
  invalidateBoundaryList?: boolean;
}): UseMutationResult<TData, ErrorNotification, TVariables> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<TData, ErrorNotification, TVariables>({
    mutationFn: config.mutationFn,
    onSuccess: (_data, variables): void => {
      setNotification({
        id: config.successId,
        message: config.successMessage,
        details: config.successDetails,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/admin/organization/${variables.organizationId}/cloud`],
        refetchType: "all",
      });
      if (config.invalidateBoundaryList) {
        queryClient.invalidateQueries({
          queryKey: ["/api/admin/customer-boundary"],
          refetchType: "all",
        });
      }
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: config.errorId,
        message: config.errorMessage,
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useAttachOrganizationBoundary = (): UseMutationResult<
  AttachCustomerBoundaryResponseBody,
  ErrorNotification,
  { organizationId: string; customerBoundaryId: string }
> =>
  useOrganizationCloudWrite({
    mutationFn: async ({ organizationId, customerBoundaryId }) => {
      const res = await axios.post(
        `/api/admin/customer-boundary/${customerBoundaryId}/organization`,
        { organizationId }
      );
      return res.data;
    },
    successId: "organization-boundary-attached",
    successMessage: "Customer Boundary Attached",
    successDetails: "The organization can now run against this boundary",
    errorId: "organization-boundary-attach-failed",
    errorMessage: "Customer Boundary Attach Failed",
    invalidateBoundaryList: true,
  });

export const useDetachOrganizationBoundary = (): UseMutationResult<
  void,
  ErrorNotification,
  { organizationId: string; organizationCustomerBoundaryId: string }
> =>
  useOrganizationCloudWrite({
    mutationFn: async ({ organizationCustomerBoundaryId }) => {
      const res = await axios.delete(
        `/api/admin/customer-boundary/organization/${organizationCustomerBoundaryId}`
      );
      return res.data;
    },
    successId: "organization-boundary-detached",
    successMessage: "Customer Boundary Detached",
    successDetails: "The attachment has been revoked",
    errorId: "organization-boundary-detach-failed",
    errorMessage: "Customer Boundary Detach Failed",
    invalidateBoundaryList: true,
  });

export const useMaterialiseOrganizationRunEnvironment = (): UseMutationResult<
  MaterialiseOrganizationRunEnvironmentResponseBody,
  ErrorNotification,
  { organizationId: string }
> =>
  useOrganizationCloudWrite({
    mutationFn: async ({ organizationId }) => {
      const res = await axios.post(
        `/api/admin/organization/${organizationId}/run-environment`
      );
      return res.data;
    },
    successId: "organization-run-environment-created",
    successMessage: "Run Environment Created",
    successDetails: "The organization now has a cloud run environment",
    errorId: "organization-run-environment-create-failed",
    errorMessage: "Run Environment Creation Failed",
  });

export const useBindOrganizationRunEnvironmentBoundary = (): UseMutationResult<
  void,
  ErrorNotification,
  {
    organizationId: string;
    profileClusterId: number;
    organizationCustomerBoundaryId: string;
  }
> =>
  useOrganizationCloudWrite({
    mutationFn: async ({
      organizationId,
      profileClusterId,
      organizationCustomerBoundaryId,
    }) => {
      const res = await axios.put(
        `/api/admin/organization/${organizationId}/run-environment/${profileClusterId}/boundary`,
        { organizationCustomerBoundaryId }
      );
      return res.data;
    },
    successId: "organization-run-environment-bound",
    successMessage: "Run Environment Bound",
    successDetails: "A launch will use the bound boundary",
    errorId: "organization-run-environment-bind-failed",
    errorMessage: "Run Environment Bind Failed",
  });

export const useUnbindOrganizationRunEnvironmentBoundary =
  (): UseMutationResult<
    void,
    ErrorNotification,
    { organizationId: string; profileClusterId: number }
  > =>
    useOrganizationCloudWrite({
      mutationFn: async ({ organizationId, profileClusterId }) => {
        const res = await axios.delete(
          `/api/admin/organization/${organizationId}/run-environment/${profileClusterId}/boundary`
        );
        return res.data;
      },
      successId: "organization-run-environment-unbound",
      successMessage: "Run Environment Unbound",
      successDetails: "The attachment stays available for a later bind",
      errorId: "organization-run-environment-unbind-failed",
      errorMessage: "Run Environment Unbind Failed",
    });

const fetchAllowedServiceAccounts = async (
  requestParams: GetAdminOrganizationAllowedServiceAccountsRequestParams
): Promise<GetAdminOrganizationAllowedServiceAccountsResponseBody> => {
  const res = await axios.get(
    `/api/admin/organization/${requestParams.organizationId}/allowed-service-accounts`
  );
  return res.data;
};

export const useGetAllowedServiceAccountsForOrganization = (
  requestParams: GetAdminOrganizationAllowedServiceAccountsRequestParams,
  enabled: boolean
): UseQueryResult<
  GetAdminOrganizationAllowedServiceAccountsResponseBody,
  Error
> => {
  return useQuery<
    GetAdminOrganizationAllowedServiceAccountsResponseBody,
    Error
  >({
    queryKey: [
      `/api/admin/organization/${requestParams.organizationId}/allowed-service-accounts`,
      requestParams,
    ],
    queryFn: () => fetchAllowedServiceAccounts(requestParams),
    enabled,
    retry: false,
  });
};

export const useCreateAllowedServiceAccountForOrganization =
  (): UseMutationResult<
    CreateAdminOrganizationAllowedServiceAccountResponseBody,
    ErrorNotification,
    CreateAdminOrganizationAllowedServiceAccountRequestParams &
      CreateAdminOrganizationAllowedServiceAccountRequestBody
  > => {
    const queryClient = useQueryClient();
    const { setNotification, NOTIFICATION } = useNotifications();

    return useMutation({
      mutationFn: async payload => {
        const res = await axios.post(
          `/api/admin/organization/${payload.organizationId}/allowed-service-accounts`,
          {
            serviceAccountEmail: payload.serviceAccountEmail,
          }
        );
        return res.data;
      },
      onSuccess: (_data, variables) => {
        setNotification({
          id: "allowed-service-account-created",
          message: "Service account added",
          details: "Allowed service account email added successfully.",
          variant: NOTIFICATION.SUCCESS,
        });
        queryClient.invalidateQueries({
          queryKey: [
            `/api/admin/organization/${variables.organizationId}/allowed-service-accounts`,
          ],
        });
      },
      onError: (error: ErrorNotification) => {
        setNotification({
          id: "allowed-service-account-create-failed",
          message: "Failed to add service account",
          details:
            error?.response?.data?.message ||
            "Allowed service account email could not be added",
          variant: NOTIFICATION.ERROR,
        });
      },
    });
  };

const fetchAllowedRoleArns = async (
  requestParams: GetAdminOrganizationAllowedRoleArnsRequestParams
): Promise<GetAdminOrganizationAllowedRoleArnsResponseBody> => {
  const res = await axios.get(
    `/api/admin/organization/${requestParams.organizationId}/allowed-role-arns`
  );
  return res.data;
};

export const useGetAllowedRoleArnsForOrganization = (
  requestParams: GetAdminOrganizationAllowedRoleArnsRequestParams,
  enabled: boolean
): UseQueryResult<GetAdminOrganizationAllowedRoleArnsResponseBody, Error> => {
  return useQuery<GetAdminOrganizationAllowedRoleArnsResponseBody, Error>({
    queryKey: [
      `/api/admin/organization/${requestParams.organizationId}/allowed-role-arns`,
      requestParams,
    ],
    queryFn: () => fetchAllowedRoleArns(requestParams),
    enabled,
    retry: false,
  });
};

export const useCreateAllowedRoleArnForOrganization = (): UseMutationResult<
  CreateAdminOrganizationAllowedRoleArnResponseBody,
  ErrorNotification,
  CreateAdminOrganizationAllowedRoleArnRequestParams &
    CreateAdminOrganizationAllowedRoleArnRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async payload => {
      const res = await axios.post(
        `/api/admin/organization/${payload.organizationId}/allowed-role-arns`,
        { roleArn: payload.roleArn }
      );
      return res.data;
    },
    onSuccess: (_data, variables) => {
      setNotification({
        id: "allowed-role-arn-created",
        message: "Role ARN added",
        details: "Allowed role ARN added successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [
          `/api/admin/organization/${variables.organizationId}/allowed-role-arns`,
        ],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "allowed-role-arn-create-failed",
        message: "Failed to add role ARN",
        details:
          error?.response?.data?.message ||
          "Allowed role ARN could not be added",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteAllowedRoleArnForOrganization = (): UseMutationResult<
  DeleteAdminOrganizationAllowedRoleArnResponseBody,
  ErrorNotification,
  DeleteAdminOrganizationAllowedRoleArnRequestParams
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async payload => {
      const res = await axios.delete(
        `/api/admin/organization/${payload.organizationId}/allowed-role-arns/${payload.allowedRoleArnId}`
      );
      return res.data;
    },
    onSuccess: (_data, variables) => {
      setNotification({
        id: "allowed-role-arn-deleted",
        message: "Role ARN removed",
        details: "Allowed role ARN removed successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [
          `/api/admin/organization/${variables.organizationId}/allowed-role-arns`,
        ],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "allowed-role-arn-delete-failed",
        message: "Failed to remove role ARN",
        details:
          error?.response?.data?.message ||
          "Allowed role ARN could not be removed",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

const fetchAllowedDirPrefixes = async (
  requestParams: GetAdminOrganizationAllowedDirPrefixesRequestParams
): Promise<GetAdminOrganizationAllowedDirPrefixesResponseBody> => {
  const res = await axios.get(
    `/api/admin/organization/${requestParams.organizationId}/allowed-dir-prefixes`
  );
  return res.data;
};

export const useGetAllowedDirPrefixesForOrganization = (
  requestParams: GetAdminOrganizationAllowedDirPrefixesRequestParams,
  enabled: boolean
): UseQueryResult<
  GetAdminOrganizationAllowedDirPrefixesResponseBody,
  Error
> => {
  return useQuery<GetAdminOrganizationAllowedDirPrefixesResponseBody, Error>({
    queryKey: [
      `/api/admin/organization/${requestParams.organizationId}/allowed-dir-prefixes`,
      requestParams,
    ],
    queryFn: () => fetchAllowedDirPrefixes(requestParams),
    enabled,
    retry: false,
  });
};

export const useCreateAllowedDirPrefixForOrganization = (): UseMutationResult<
  CreateAdminOrganizationAllowedDirPrefixResponseBody,
  ErrorNotification,
  CreateAdminOrganizationAllowedDirPrefixRequestParams &
    CreateAdminOrganizationAllowedDirPrefixRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async payload => {
      const res = await axios.post(
        `/api/admin/organization/${payload.organizationId}/allowed-dir-prefixes`,
        {
          directoryKind: payload.directoryKind,
          pathPrefix: payload.pathPrefix,
        }
      );
      return res.data;
    },
    onSuccess: (_data, variables) => {
      setNotification({
        id: "allowed-dir-prefix-created",
        message: "Directory prefix added",
        details: "Allowed directory prefix added successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [
          `/api/admin/organization/${variables.organizationId}/allowed-dir-prefixes`,
        ],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "allowed-dir-prefix-create-failed",
        message: "Failed to add directory prefix",
        details:
          error?.response?.data?.message ||
          "Allowed directory prefix could not be added",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteAllowedDirPrefixForOrganization = (): UseMutationResult<
  DeleteAdminOrganizationAllowedDirPrefixResponseBody,
  ErrorNotification,
  DeleteAdminOrganizationAllowedDirPrefixRequestParams
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async payload => {
      const res = await axios.delete(
        `/api/admin/organization/${payload.organizationId}/allowed-dir-prefixes/${payload.allowedDirPrefixId}`
      );
      return res.data;
    },
    onSuccess: (_data, variables) => {
      setNotification({
        id: "allowed-dir-prefix-deleted",
        message: "Directory prefix removed",
        details: "Allowed directory prefix removed successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [
          `/api/admin/organization/${variables.organizationId}/allowed-dir-prefixes`,
        ],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "allowed-dir-prefix-delete-failed",
        message: "Failed to remove directory prefix",
        details:
          error?.response?.data?.message ||
          "Allowed directory prefix could not be removed",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteAllowedServiceAccountForOrganization =
  (): UseMutationResult<
    DeleteAdminOrganizationAllowedServiceAccountResponseBody,
    ErrorNotification,
    DeleteAdminOrganizationAllowedServiceAccountRequestParams
  > => {
    const queryClient = useQueryClient();
    const { setNotification, NOTIFICATION } = useNotifications();

    return useMutation({
      mutationFn: async payload => {
        const res = await axios.delete(
          `/api/admin/organization/${payload.organizationId}/allowed-service-accounts/${payload.allowedServiceAccountId}`
        );
        return res.data;
      },
      onSuccess: (_data, variables) => {
        setNotification({
          id: "allowed-service-account-deleted",
          message: "Service account removed",
          details: "Allowed service account email removed successfully.",
          variant: NOTIFICATION.SUCCESS,
        });
        queryClient.invalidateQueries({
          queryKey: [
            `/api/admin/organization/${variables.organizationId}/allowed-service-accounts`,
          ],
        });
      },
      onError: (error: ErrorNotification) => {
        setNotification({
          id: "allowed-service-account-delete-failed",
          message: "Failed to remove service account",
          details:
            error?.response?.data?.message ||
            "Allowed service account email could not be removed",
          variant: NOTIFICATION.ERROR,
        });
      },
    });
  };

// ============================================
// Organization Members (Admin)
// ============================================

/**
 * One query key serves all four hooks below, so adding, removing, or re-roling a member refetches
 * the roles alongside the members. That matters because role ids are scoped to a single
 * organization, and a stale role id names nothing the server will accept.
 */
const membersQueryKey = (organizationId: string): string[] => [
  `/api/admin/organization/${organizationId}/users`,
];

export const useGetOrganizationMembers = (
  params: GetAdminOrganizationMembersRequestParams,
  enabled: boolean
): UseQueryResult<GetAdminOrganizationMembersResponseBody, Error> => {
  return useQuery<GetAdminOrganizationMembersResponseBody, Error>({
    queryKey: membersQueryKey(params.organizationId),
    queryFn: async () => {
      const res = await axios.get(
        `/api/admin/organization/${params.organizationId}/users`
      );
      return res.data;
    },
    enabled,
    retry: false,
  });
};

export const useAddOrganizationMember = (): UseMutationResult<
  AddAdminOrganizationMemberResponseBody,
  ErrorNotification,
  AddAdminOrganizationMemberRequestParams &
    AddAdminOrganizationMemberRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async payload => {
      const res = await axios.post(
        `/api/admin/organization/${payload.organizationId}/users`,
        { email: payload.email, roleId: payload.roleId }
      );
      return res.data;
    },
    onSuccess: (_data, variables) => {
      setNotification({
        id: "organization-member-added",
        message: "Member added",
        details: `${variables.email} was added to the organization.`,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: membersQueryKey(variables.organizationId),
      });
      // The organization list carries its own copy of the members, so the count on the row behind
      // this modal is stale until it refetches too.
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/organization"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-member-add-failed",
        message: "Failed to add member",
        details:
          error?.response?.data?.message ||
          "The account could not be added to this organization",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateOrganizationMemberRole = (): UseMutationResult<
  UpdateAdminOrganizationMemberResponseBody,
  ErrorNotification,
  UpdateAdminOrganizationMemberRequestParams &
    UpdateAdminOrganizationMemberRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async payload => {
      const res = await axios.patch(
        `/api/admin/organization/${payload.organizationId}/users/${payload.userId}`,
        { roleId: payload.roleId }
      );
      return res.data;
    },
    onSuccess: (_data, variables) => {
      setNotification({
        id: "organization-member-role-updated",
        message: "Role updated",
        details: "The member's role was changed.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: membersQueryKey(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/organization"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-member-role-update-failed",
        message: "Failed to change role",
        details:
          error?.response?.data?.message || "The role could not be changed",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useRemoveOrganizationMember = (): UseMutationResult<
  RemoveAdminOrganizationMemberResponseBody,
  ErrorNotification,
  RemoveAdminOrganizationMemberRequestParams
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async payload => {
      const res = await axios.delete(
        `/api/admin/organization/${payload.organizationId}/users/${payload.userId}`
      );
      return res.data;
    },
    onSuccess: (_data, variables) => {
      setNotification({
        id: "organization-member-removed",
        message: "Member removed",
        details: "The account was removed from this organization.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: membersQueryKey(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/organization"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-member-remove-failed",
        message: "Failed to remove member",
        details:
          error?.response?.data?.message ||
          "The account could not be removed from this organization",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

// ============================================
// Organization run environment definitions
// ============================================

const runEnvironmentsQueryKey = (organizationId: string): string =>
  `/api/admin/organization/${organizationId}/run-environments`;

const organizationCredentialsQueryKey = (organizationId: string): string =>
  `/api/admin/organization/${organizationId}/credentials`;

/**
 * Narrows a form payload to the fields these routes accept.
 *
 * Not defensive tidying: the write routes validate with `Joi.object`, which answers 400 on an
 * unknown key, and the form carries plenty the server resolves for itself (`id`, `ownerId`,
 * `perms`, `groupId`, `isSystemGenerated`). Without this projection every save from the console
 * would fail on a field the administrator never touched. The field list is shared with the route
 * and pinned by a backend test, so the two cannot drift apart.
 */
export const projectAdminRunEnvironmentPayload = (
  payload: Partial<ProfileClusterForClient>,
  options: { systemGenerated?: boolean } = {}
): Record<string, unknown> => {
  const allowed: readonly string[] = options.systemGenerated
    ? ADMIN_SYSTEM_GENERATED_WRITABLE_FIELDS
    : ADMIN_ORGANIZATION_RUN_ENVIRONMENT_WRITABLE_FIELDS;

  return Object.fromEntries(
    Object.entries(payload).filter(
      ([field, value]) => allowed.includes(field) && value !== undefined
    )
  );
};

export const useGetAdminOrganizationRunEnvironments = (
  organizationId: string,
  enabled: boolean
): UseQueryResult<ListAdminOrganizationRunEnvironmentsResponseBody, Error> =>
  useQuery<ListAdminOrganizationRunEnvironmentsResponseBody, Error>({
    queryKey: [runEnvironmentsQueryKey(organizationId)],
    queryFn: async () => {
      const res = await axios.get(runEnvironmentsQueryKey(organizationId));
      return res.data;
    },
    enabled,
    retry: false,
  });

/**
 * One definition in full, fetched only when a drawer opens.
 *
 * The listing is a lean projection, so this is what the edit form prefills from. Fetching it lazily
 * keeps the tab's first paint to one request.
 */
export const useGetAdminOrganizationRunEnvironment = (
  organizationId: string,
  runEnvironmentId: number | undefined
): UseQueryResult<GetAdminOrganizationRunEnvironmentResponseBody, Error> =>
  useQuery<GetAdminOrganizationRunEnvironmentResponseBody, Error>({
    queryKey: [
      `${runEnvironmentsQueryKey(organizationId)}/${runEnvironmentId}`,
    ],
    queryFn: async () => {
      const res = await axios.get(
        `${runEnvironmentsQueryKey(organizationId)}/${runEnvironmentId}`
      );
      return res.data;
    },
    enabled: !!organizationId && !!runEnvironmentId,
    retry: false,
  });

export const useGetAdminOrganizationCredentials = (
  organizationId: string,
  enabled: boolean
): UseQueryResult<ListAdminOrganizationCredentialsResponseBody, Error> =>
  useQuery<ListAdminOrganizationCredentialsResponseBody, Error>({
    queryKey: [organizationCredentialsQueryKey(organizationId)],
    queryFn: async () => {
      const res = await axios.get(
        organizationCredentialsQueryKey(organizationId)
      );
      return res.data;
    },
    enabled,
    retry: false,
  });

/**
 * The three writes.
 *
 * Each refreshes the run environments list AND the organization's cloud query, because materialise
 * and bind write the same table from the Cloud tab: a definition removed here can be the row that
 * tab is describing.
 */
const useRunEnvironmentWrite = <
  TVariables extends { organizationId: string },
>(config: {
  mutationFn: (variables: TVariables) => Promise<unknown>;
  successId: string;
  successMessage: string;
  successDetails: string;
  errorId: string;
  errorMessage: string;
}): UseMutationResult<unknown, ErrorNotification, TVariables> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<unknown, ErrorNotification, TVariables>({
    mutationFn: config.mutationFn,
    onSuccess: (_data, variables): void => {
      setNotification({
        id: config.successId,
        message: config.successMessage,
        details: config.successDetails,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [runEnvironmentsQueryKey(variables.organizationId)],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/admin/organization/${variables.organizationId}/cloud`],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: config.errorId,
        message: config.errorMessage,
        // The server's own message carries the reason a write was refused: a platform managed row,
        // a credential belonging elsewhere, an organization with no administrator to own the row,
        // or a foreign key still pointing at it. Losing it here would leave every refusal looking
        // the same to the operator.
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useCreateAdminOrganizationRunEnvironment = () =>
  useRunEnvironmentWrite<{
    organizationId: string;
    payload: Partial<ProfileClusterForClient>;
  }>({
    mutationFn: async ({ organizationId, payload }) =>
      (
        await axios.post(
          runEnvironmentsQueryKey(organizationId),
          projectAdminRunEnvironmentPayload(payload)
        )
      ).data,
    successId: "admin-run-environment-created",
    successMessage: "Run environment created",
    successDetails: "The organization can now select it when launching a run.",
    errorId: "admin-run-environment-create-failed",
    errorMessage: "Failed to create the run environment",
  });

export const useUpdateAdminOrganizationRunEnvironment = () =>
  useRunEnvironmentWrite<{
    organizationId: string;
    runEnvironmentId: number;
    payload: Partial<ProfileClusterForClient>;
    systemGenerated?: boolean;
  }>({
    mutationFn: async ({
      organizationId,
      runEnvironmentId,
      payload,
      systemGenerated,
    }) =>
      (
        await axios.patch(
          `${runEnvironmentsQueryKey(organizationId)}/${runEnvironmentId}`,
          projectAdminRunEnvironmentPayload(payload, { systemGenerated })
        )
      ).data,
    successId: "admin-run-environment-updated",
    successMessage: "Run environment updated",
    successDetails: "The change applies to the organization's next run.",
    errorId: "admin-run-environment-update-failed",
    errorMessage: "Failed to update the run environment",
  });

export const useDeleteAdminOrganizationRunEnvironment = () =>
  useRunEnvironmentWrite<{
    organizationId: string;
    runEnvironment: AdminOrganizationRunEnvironment;
  }>({
    mutationFn: async ({ organizationId, runEnvironment }) =>
      (
        await axios.delete(
          `${runEnvironmentsQueryKey(organizationId)}/${runEnvironment.id}`
        )
      ).data,
    successId: "admin-run-environment-deleted",
    successMessage: "Run environment deleted",
    successDetails: "It is no longer available to the organization.",
    errorId: "admin-run-environment-delete-failed",
    errorMessage: "Failed to delete the run environment",
  });
